import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import { ContentSection, ContentValue, ContentSectionGroup } from "@fabric-types/svc-content";
import { Store } from "@ngrx/store";
import { CoreContentState } from "@obg/ngx.core.content";
import { coerceBoolean, DocumentRef } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { find, head } from "lodash-es";
import { Observable, Subscription } from "rxjs";
import { tap } from "rxjs/operators";

@Component({
	selector: "obg-content-section-group-container",
	templateUrl: "./content-section-group.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class ContentSectionGroupContainer implements OnDestroy, OnChanges {
	@Input()
	@coerceBoolean()
	anchorLinksVisible = true;

	@Input()
	@coerceBoolean()
	enableTableFormatting = false;

	@Input()
	@coerceBoolean()
	seoEnabled = false;

	@Input()
	@coerceBoolean()
	hideBanner = false;

	@Input()
	@coerceBoolean()
	hideTitles = false;

	@Input() document: string | undefined;
	@Input() documentGroupKey: string | undefined;

	@Input() keysToBeUsedWithinDocumentType: string[] | undefined;

	subtitle: string | undefined;
	contentSections: ContentSection[] | undefined;
	bannerKey: string | undefined;

	private pageSubtitleInfix = "-subtitle-";
	private contentSectionGroup: ContentSectionGroup | undefined;

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreContentState>,
		private changeDetector: ChangeDetectorRef,
		private documentRef: DocumentRef
	) {}

	ngOnChanges(changes: {
		documentGroupKey?: SimpleChange;
		document?: SimpleChange;
		keysToBeUsedWithinDocumentType?: SimpleChange;
	}) {
		if (changes.document && this.document) {
			this.setDocument(this.document);
			return;
		}

		if (changes.documentGroupKey && this.contentSectionGroup) {
			this.updateSection(this.contentSectionGroup);
		}

		if (changes.keysToBeUsedWithinDocumentType && this.contentSections) {
			this.contentSections = this.filterSections(this.contentSections);
		}
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	private setDocument(document: string) {
		this.data$$?.unsubscribe();

		this.data$$ = new Observable(
			this.documentRef.obgEventBus.retrieve(ContentValue.getContentSectionGroup, {
				key: document,
			})
		)
			.pipe(
				tap((contentSectionGroup) => {
					this.contentSectionGroup = contentSectionGroup;
					this.updateSection(contentSectionGroup);
					this.changeDetector.markForCheck();
				})
			)
			.subscribe();
	}

	private updateSection(contentSectionGroup: ContentSectionGroup) {
		const key = this.documentGroupKey || head(Object.keys(contentSectionGroup))!;
		const sections = this.filterSections(contentSectionGroup[key]);
		const titleSection = find(sections, (s) => s.documentKey.indexOf(this.pageSubtitleInfix) > -1);

		this.bannerKey = `multiproduct-contentbanner.${key}`;
		this.subtitle = titleSection ? titleSection.title?.text : undefined;

		// any document containing the page subtitle (e.g. terms & conditions version) should not be rendered in the list
		this.contentSections = sections.filter((s) => s.documentKey.indexOf(this.pageSubtitleInfix) === -1);
		this.store.dispatch(RouteAction.scrollToCurrentFragment());
	}

	private filterSections(sections: ContentSection[]): ContentSection[] {
		if (!this.keysToBeUsedWithinDocumentType?.length) {
			return sections;
		}

		return sections?.filter((s) => this.keysToBeUsedWithinDocumentType?.includes(s.documentKey));
	}
}
